import {
  aabbCenter,
  bodyAabb,
  createEntity,
  ENTITY_PHASE_SENSOR,
  type EntityInstance,
  entityAabb,
} from '@md/engine';
import type { EntityDef } from '@md/shared';
import { LEVER_MIN_DISTANCE_TILES, LEVER_RANGE_TILES } from '@md/shared';
import { polarityOf } from '../abilities/magnet-field.js';
import { fieldReaches } from '../forces.js';
import { LeverDefSchema, polaritySign } from './schemas.js';

/**
 * Levier polarisé (concept §3.2).
 *
 * « Ne peut être actionné qu'en l'attirant / le repoussant à distance » : il
 * bascule sur le FRONT MONTANT d'un champ actif du bon pôle, et seulement si
 * le robot est à plus de `LEVER_MIN_DISTANCE_TILES` — se coller contre lui ne
 * sert à rien. L'état est un interrupteur bistable : il reste dans sa position
 * après le départ du robot.
 */
export function createPolarizedLever(def: EntityDef): EntityInstance {
  const cfg = LeverDefSchema.parse(def);
  const sign = polaritySign(cfg.polarity);

  return createEntity({
    id: cfg.id,
    type: 'lever',
    x: cfg.x + 0.25,
    y: cfg.y + 0.2,
    w: 0.5,
    h: 0.8,
    phase: ENTITY_PHASE_SENSOR,
    flags: { active: 0, polarity: sign, charged: 0 },
    appearance: {
      shape: 'lever',
      tint: sign > 0 ? 0x8c4a4f : 0x3f5f8c,
      activeTint: sign > 0 ? 0xff9d9d : 0x9fd8ff,
    },
    update(self, ctx) {
      const center = aabbCenter(entityAabb(self));
      let charged = false;
      for (const player of ctx.players) {
        if (player.dead || !player.fieldActive || polarityOf(player) !== sign) {
          continue;
        }
        const from = aabbCenter(bodyAabb(player.body));
        const distance = Math.hypot(center.x - from.x, center.y - from.y);
        if (
          distance >= LEVER_MIN_DISTANCE_TILES &&
          distance <= LEVER_RANGE_TILES &&
          fieldReaches(ctx.level, from, center)
        ) {
          charged = true;
          break;
        }
      }
      const wasCharged = self.flags.charged === 1;
      if (charged && !wasCharged) {
        self.flags.active = self.flags.active === 1 ? 0 : 1;
      }
      self.flags.charged = charged ? 1 : 0;
    },
  });
}
